// Build a `photohawk.scan/v1` sidecar from on-device detections — the exact
// contract the backend already validates in
// photohawk-serverless/src/layers/lib/src/prescanned.js (validateSidecar).
//
// The web uploader would: upload each face/person CROP to the scanning bucket,
// then reference its key here; upload this JSON to the scanning-results bucket;
// and PUT the original with x-amz-meta-prescanned:v1 + x-amz-meta-scan-manifest.
// In this prototype we don't upload crops, so `key` is a placeholder — the JSON
// is for eyeballing shape/parity, not ingestion.

export const SCHEMA = 'photohawk.scan/v1';

// Must be one of prescanned.js KNOWN_FACE_MODELS / KNOWN_PERSON_MODELS or the
// backend falls back to a server scan (the model-version escape hatch, §9/§10).
export const FACE_MODEL_VERSION = 'yolov8n-face@1';
export const PERSON_MODEL_VERSION = 'yolov8n@1';
// On-device text (bib/OCR) model version — must match ppocrConfig.TEXT_MODEL_VERSION
// and the future prescanned.js KNOWN_TEXT_MODELS entry (Phase 2 trust hatch, spec §4b).
export const TEXT_MODEL_VERSION = 'ppocr-en-det-v3-rec-v4@1';

export function buildSidecar({ faces = [], people = [], text = [], faceModel = FACE_MODEL_VERSION, personModel = PERSON_MODEL_VERSION, textModel = TEXT_MODEL_VERSION } = {}) {
  const modelVersions = {};
  if (faces.length > 0) modelVersions.face = faceModel;
  if (people.length > 0) modelVersions.person = personModel;
  // Advertise the text model only when some person actually carries OCR text, so the
  // server can tell a text-prescanned sidecar from a faces/people-only one.
  if (people.some((p) => (p.text || []).length > 0) || text.length > 0) modelVersions.text = textModel;

  return {
    schema: SCHEMA,
    faces: faces.map((f, i) => ({
      key: f.key || `PENDING-CROP-KEY/face/${i}.jpg`, // real run: the uploaded crop's key
      boundingBox: f.box,
      // Actual crop pixel dims (capped at the face crop cap) — scanFacesBatch reads
      // face.width/height to lay the montage out; without them the layout is NaN
      // and Rekognition faces can't be mapped back to their photo.
      width: f.width,
      height: f.height,
      confidence: f.confidence,
      // The relative FFT sharpness score the client kept this face on (999 if sole
      // face). Lets the server's sampled-verification compare against its own filter.
      sharpnessLevel: f.sharpnessLevel,
    })),
    people: people.map((p, i) => ({
      key: p.key || `PENDING-CROP-KEY/person/${i}.jpg`,
      boundingBox: p.box,
      width: p.width,
      height: p.height,
      confidence: p.confidence,
      sharpnessLevel: p.sharpnessLevel,
      text: p.text || [],
    })),
    text,
    modelVersions,
  };
}
